import React, { useCallback, useEffect, useRef } from "react";
import { styled } from "goober";

import { useClientOnly } from "@/util/ClientOnly.jsx";
import globals from "@/util/global-whitelist.mjs";

export const RippleContainer = styled("div", React.forwardRef)<{
  $color: string;
  $duration: number;
}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  border-radius: inherit;

  span {
    transform: scale(0);
    border-radius: 100%;
    position: absolute;
    opacity: 0.15;
    background-color: ${(props) => props.$color};
    animation-name: ripple;
    animation-duration: ${(props) => props.$duration}ms;
    animation-timing-function: cubic-bezier(0.25, 1, 0.5, 1); /* quadric out */
    pointer-events: none;
  }

  @keyframes ripple {
    to {
      opacity: 0;
      transform: scale(var(--ripple-scale, 2));
    }
  }
`;

/**
 * Create a styled ripple span positioned relative to
 * the given element. The span will ripple out
 * covering the full size of this element.
 */
function createRippleSpan({ element, pageX, pageY }) {
  const span = globals.document.createElement("span");

  const { width, height, x, y } = element.getBoundingClientRect();
  const size = width > height ? width : height;

  span.style.top = `${pageY - y - size / 2}px`;
  span.style.left = `${pageX - x - size / 2}px`;
  span.style.width = `${size}px`;
  span.style.height = `${size}px`;

  return span;
}

const Ripple = ({ duration = 1000, color = "var(--shade0)" }) => {
  // the element we append ripple spans to
  const container = useRef(null);
  // holds references to all spans and timeouts for cleanup
  const cleanup = useRef(new Map());

  // append a ripple span to the ref and removes it after our duration
  const addRipple = useCallback(
    (
      event:
        | React.MouseEvent<HTMLDivElement>
        | React.TouchEvent<HTMLDivElement>,
    ) => {
      if (!container.current) return;

      const { currentTarget: element } = event;
      const { pageX, pageY } = "pageX" in event ? event : event.touches[0];

      // assign to a reference so we can clear the span after the timeout
      // otherwise we keep references to detached spans until this component
      // unmounts
      const ref = {
        id: null,
        span: createRippleSpan({ element, pageX, pageY }),
      };

      ref.id = setTimeout(() => {
        ref.span.remove();
        ref.span = null;
        cleanup.current.delete(ref.id);
      }, duration);

      container.current.appendChild(ref.span);
      cleanup.current.set(ref.id, ref);
    },
    [duration],
  );

  useEffect(() => {
    const map = cleanup.current;

    return () => {
      map.forEach(({ span, id }) => {
        span?.remove();
        clearTimeout(id);
        map.delete(id);
      });
    };
  }, []);

  const isHydrated = useClientOnly();

  if (!isHydrated) return null;

  return (
    <RippleContainer
      ref={container}
      $duration={duration}
      $color={color}
      onPointerDown={addRipple}
    />
  );
};

export default Ripple;
